import React, { useContext, useEffect, useState } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter, Button, Table,
    Nav, NavItem, NavLink, TabContent, TabPane, Badge
} from 'reactstrap';
import classNames from 'classnames';
import { ContaReceberContext } from '../../context/ContaReceberContext';
import { ContaReceberDetalhesType } from '../../context/hooks/useContaReceber';

interface DetalhesContaReceberModalProps {
    isOpen: boolean;
    toggle: () => void;
    contaReceberId: number | null;
}

const moeda = (valor?: number | null) =>
    typeof valor === 'number'
        ? valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : '-';

// Vencimento vem como DateOnly ("2026-08-17"); datas com hora vêm como ISO completo.
const data = (valor?: string | null) => {
    if (!valor) return '-';
    const [somenteData] = valor.split('T');
    const partes = somenteData.split('-');
    return partes.length === 3 ? partes.reverse().join('/') : valor;
};

const Campo = ({ label, valor }: { label: string; valor?: string | number | null }) => (
    <div>
        <strong>{label}:</strong>{' '}
        {valor === null || valor === undefined || valor === '' ? '-' : valor}
    </div>
);

export const DetalhesContaReceberModal = ({ isOpen, toggle, contaReceberId }: DetalhesContaReceberModalProps) => {
    const { getContaReceberDetalhes, baixarBoletos } = useContext(ContaReceberContext);
    const [detalhes, setDetalhes] = useState<ContaReceberDetalhesType | null>(null);
    const [activeTab, setActiveTab] = useState('parcelas');

    useEffect(() => {
        if (!isOpen || contaReceberId == null) {
            setDetalhes(null);
            setActiveTab('parcelas');
            return;
        }
        getContaReceberDetalhes(contaReceberId).then(setDetalhes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, contaReceberId]);

    const aluno = detalhes?.aluno;
    const cliente = detalhes?.cliente;
    const contrato = detalhes?.contrato;
    const financeiro = detalhes?.financeiro;

    const telefonesAluno = [
        aluno?.landline, aluno?.telephone_01, aluno?.telephone_02,
        aluno?.telephone_03, aluno?.telephone_04, aluno?.telephone_05, aluno?.telephone_06,
    ].filter(Boolean);

    const aba = (id: string, titulo: string) => (
        <NavItem>
            <NavLink
                className={classNames({ active: activeTab === id })}
                onClick={() => setActiveTab(id)}
                style={{ cursor: 'pointer' }}
            >
                {titulo}
            </NavLink>
        </NavItem>
    );

    return (
        <Modal isOpen={isOpen} toggle={toggle} className="custom-modal-xl" scrollable>
            <ModalHeader toggle={toggle}>
                Informações completas
                {aluno && <> — <strong>{aluno.name}</strong></>}
                {aluno?.referenceCode ? <span className="text-muted"> (cód. {aluno.referenceCode})</span> : null}
                {detalhes?.permissaoVendaRestricao && (
                    <Badge color="danger" className="ml-2" style={{ verticalAlign: 'middle', fontSize: '0.75rem' }}>
                        <i className="fa-solid fa-triangle-exclamation mr-1"></i> Permissão de Venda com Restrição
                    </Badge>
                )}
            </ModalHeader>
            <ModalBody>
                {!detalhes ? (
                    <div className="text-center text-muted py-4">Carregando informações...</div>
                ) : (
                    <>
                        {detalhes.permissaoVendaRestricao && (
                            <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center" role="alert" style={{ fontSize: '0.85rem' }}>
                                <i className="fa-solid fa-triangle-exclamation fa-lg mr-2"></i>
                                <div>
                                    <strong>Atenção:</strong> Este cliente/aluno possui <strong>permissão de venda com restrição</strong> (bloqueio ou pendência financeira na cobrança).
                                </div>
                            </div>
                        )}
                        {financeiro && (
                            <div className="aluno-details-grid mb-3" style={{ border: '1px solid #e9ecef', borderRadius: 4 }}>
                                <Campo label="Valor total" valor={moeda(financeiro.valorTotal)} />
                                <Campo label="Entrada" valor={moeda(financeiro.entradaValor)} />
                                <Campo label="Parcelas" valor={financeiro.quantidadeParcelas} />
                                <Campo label="Pagas" valor={financeiro.parcelasPagas} />
                                <Campo label="Pendentes" valor={financeiro.parcelasPendentes} />
                                <div>
                                    <strong>Em atraso:</strong>{' '}
                                    {financeiro.parcelasEmAtraso > 0
                                        ? <Badge color="danger">{financeiro.parcelasEmAtraso}</Badge>
                                        : '0'}
                                </div>
                                <Campo label="Valor pago" valor={moeda(financeiro.valorPago)} />
                                <Campo label="Em aberto" valor={moeda(financeiro.valorEmAberto)} />
                            </div>
                        )}

                        <Nav tabs>
                            {aba('parcelas', 'Parcelas')}
                            {aba('cliente', 'Cliente')}
                            {aba('aluno', 'Aluno')}
                            {aba('contrato', 'Contrato')}
                            {aba('venda', 'Venda')}
                        </Nav>

                        <TabContent activeTab={activeTab} className="pt-3">
                            <TabPane tabId="parcelas">
                                {!financeiro || financeiro.parcelas.length === 0 ? (
                                    <div className="text-muted">Esta conta não tem parcelas de boleto vinculadas.</div>
                                ) : (
                                    <Table size="sm" responsive className="table-striped">
                                        <thead>
                                            <tr>
                                                <th>Parcela</th>
                                                <th>Vencimento</th>
                                                <th>Valor</th>
                                                <th>Pago</th>
                                                <th>Status</th>
                                                <th>Linha digitável</th>
                                                <th>Boleto</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {financeiro.parcelas.map(parcela => (
                                                <tr
                                                    key={parcela.contaReceberId}
                                                    style={parcela.contaReceberId === detalhes.contaReceberId
                                                        ? { fontWeight: 600, backgroundColor: '#e3f2fd' }
                                                        : undefined}
                                                >
                                                    <td>{parcela.numeroParcela}/{parcela.totalParcelas}</td>
                                                    <td>{data(parcela.vencimento)}</td>
                                                    <td>{moeda(parcela.valor)}</td>
                                                    <td>{parcela.valorPago ? moeda(parcela.valorPago) : '-'}</td>
                                                    <td>
                                                        {parcela.status === 1 && <Badge color="success">Concluído</Badge>}
                                                        {parcela.status !== 1 && parcela.emAtraso && (
                                                            <Badge color="danger">Em atraso ({parcela.diasAtraso}d)</Badge>
                                                        )}
                                                        {parcela.status !== 1 && !parcela.emAtraso && (
                                                            <Badge color="warning">Pendente</Badge>
                                                        )}
                                                    </td>
                                                    <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                                                        {parcela.linhaDigitavel ?? '-'}
                                                    </td>
                                                    <td>
                                                        {parcela.temBoleto && (
                                                            <Button
                                                                size="sm"
                                                                title="Baixar boleto desta parcela"
                                                                onClick={() => baixarBoletos(
                                                                    [parcela.contaReceberId],
                                                                    'pdf',
                                                                    `boleto_parcela_${parcela.numeroParcela}de${parcela.totalParcelas}.pdf`
                                                                )}
                                                            >
                                                                <i className="fa-solid fa-barcode"></i>
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </TabPane>

                            <TabPane tabId="cliente">
                                {!cliente ? (
                                    <div className="text-muted">Nenhum cliente vinculado a esta conta.</div>
                                ) : (
                                    <div className="aluno-details-grid">
                                        <Campo label="Nome" valor={cliente.name} />
                                        <Campo label="CPF" valor={cliente.cpf} />
                                        <Campo label="RG" valor={cliente.rg} />
                                        <Campo label="Órgão emissor" valor={cliente.orgaoEmissor} />
                                        <Campo label="Data de nascimento" valor={cliente.dataNascimento} />
                                        <Campo label="E-mail" valor={cliente.email} />
                                        <Campo label="Telefone" valor={cliente.telefone} />
                                        <Campo label="Celular" valor={cliente.celular} />
                                        <Campo label="Endereço" valor={cliente.endereco} />
                                        <Campo label="Número" valor={cliente.numero} />
                                        <Campo label="Complemento" valor={cliente.complemento} />
                                        <Campo label="Bairro" valor={cliente.bairro} />
                                        <Campo label="Cidade" valor={cliente.cidade} />
                                        <Campo label="Estado" valor={cliente.estado} />
                                        <Campo label="CEP" valor={cliente.cep} />
                                        <Campo label="Nacionalidade" valor={cliente.nacionalidade} />
                                        <Campo label="Estado civil" valor={cliente.estadoCivil} />
                                        <Campo label="Profissão" valor={cliente.profissao} />
                                    </div>
                                )}
                            </TabPane>

                            <TabPane tabId="aluno">
                                {!aluno ? (
                                    <div className="text-muted">Nenhum aluno vinculado a esta conta.</div>
                                ) : (
                                    <div className="aluno-details-grid">
                                        <Campo label="Nome" valor={aluno.name} />
                                        <Campo label="Código" valor={aluno.referenceCode} />
                                        <Campo label="CPF" valor={aluno.cpf} />
                                        <Campo label="E-mail" valor={aluno.email} />
                                        <Campo label="Telefones" valor={telefonesAluno.join(' · ')} />
                                        <Campo label="Endereço" valor={aluno.address} />
                                        <Campo label="Número" valor={aluno.addressNumber} />
                                        <Campo label="Bairro" valor={aluno.neighborhood} />
                                        <Campo label="Cidade" valor={aluno.city} />
                                        <Campo label="Estado" valor={aluno.state} />
                                        <Campo label="CEP" valor={aluno.zipCode} />
                                        <Campo label="Facebook" valor={aluno.facebook} />
                                        <Campo label="Instagram" valor={aluno.instagram} />
                                    </div>
                                )}
                            </TabPane>

                            <TabPane tabId="contrato">
                                {!contrato ? (
                                    <div className="text-muted">Nenhum contrato vinculado a este aluno.</div>
                                ) : (
                                    <div className="aluno-details-grid">
                                        <Campo label="Contrato" valor={contrato.description} />
                                        <Campo label="Turma" valor={contrato.class} />
                                        <Campo label="Curso" valor={contrato.cursoNome} />
                                        <Campo label="Instituição" valor={contrato.instituicaoNome} />
                                        <Campo
                                            label="Cidade/UF"
                                            valor={[contrato.instituicaoCidade, contrato.instituicaoEstado].filter(Boolean).join('/')}
                                        />
                                        <Campo label="Status" valor={contrato.statusDescricao} />
                                        <Campo label="Alunos contratados" valor={contrato.hiredStudents} />
                                        <Campo label="Alunos atuais" valor={contrato.currentStudents} />
                                        <Campo label="Criação" valor={data(contrato.creationDate)} />
                                        <Campo label="Conclusão" valor={data(contrato.completionDate)} />
                                        <Campo label="Baixa" valor={data(contrato.dateOfDischarge)} />
                                        <Campo label="Valor total (contrato)" valor={moeda(contrato.totalValue)} />
                                        <Campo label="Entrada (contrato)" valor={moeda(contrato.downPaymentValue)} />
                                        <Campo label="Qtd. parcelas (contrato)" valor={contrato.installmentsCount} />
                                        <Campo label="Valor da parcela" valor={moeda(contrato.installmentValue)} />
                                        <Campo label="Dia de vencimento" valor={contrato.paymentDueDay} />
                                    </div>
                                )}
                            </TabPane>

                            <TabPane tabId="venda">
                                {!financeiro ? (
                                    <div className="text-muted">Esta conta não veio de uma venda.</div>
                                ) : (
                                    <>
                                        <div className="aluno-details-grid">
                                            <Campo label="Venda" valor={financeiro.vendaId} />
                                            <Campo label="Produto" valor={financeiro.produtoNome} />
                                            <Campo label="Vendedor" valor={financeiro.vendedorNome} />
                                            <Campo label="Valor negociado" valor={moeda(financeiro.valorNegociado)} />
                                        </div>
                                        <Table size="sm" responsive className="table-striped mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Forma de pagamento</th>
                                                    <th>Valor</th>
                                                    <th>Tipo</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {financeiro.formasPagamento.map((forma, i) => (
                                                    <tr key={i}>
                                                        <td>{forma.nome ?? '-'}</td>
                                                        <td>{moeda(forma.valor)}</td>
                                                        <td>{forma.boleto ? 'Parcelado (boleto)' : 'Entrada'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </>
                                )}
                            </TabPane>
                        </TabContent>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button onClick={toggle}>Fechar</Button>
            </ModalFooter>
        </Modal>
    );
};

export default DetalhesContaReceberModal;
